import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import {
    CardList,
    PortalMetricCard,
    PortalMetricGrid,
    PortalMetricLabel,
    PortalMetricValue,
    PortalPaymentOptionGrid,
    PortalPaymentSummaryAmount,
    PortalPaymentSummaryCard,
    PortalPaymentSummaryLabel,
    PortalPaymentSummaryRow,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalFormGrid,
    StandardModalHeading,
    StandardModalInfoCard,
    StandardModalSection,
    StandardModalText,
    Toast
} from "src/components";
import AirwallexForm, { AirwallexProduct } from "src/scenes/Subscription/components/AirwallexForm";
import { PaymentSelectionButton, PaymentSelectionIcon } from "src/scenes/Subscription/forms/Review";
import { SettingContext } from "../SettingContext";
import {
    cancelStorageDowngrade,
    getStorageAddOns,
    purchaseStorageUpgrade,
    scheduleStorageDowngrade
} from "../api/storageApi";

const PAYMENT_METHOD = {
    EXISTING: "existing",
    NEW: "new"
};

const ConfirmWrap = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    text-align: center;
`;

const TransitionGrid = styled.div`
    align-items: stretch;
    display: grid;
    gap: 1rem;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    width: 100%;

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
`;

const TransitionArrow = styled.div`
    align-self: center;
    color: #8E50A8;
    font-size: 1.75rem;
    font-weight: 800;
`;

function formatDate(value) {
    if (!value) {
        return "your next billing date";
    }

    return new Date(value).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric"
    });
}

function formatMoney(value, currency) {
    return `${currency} $${Number(value || 0).toFixed(2)}`;
}

export default function StorageUpgradeModal({ show, onClose, summary, onPurchased }) {
    const { token, companyName, email, abn } = useContext(SettingContext);
    const [options, setOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD.EXISTING);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        async function loadAddOns() {
            try {
                setOptions(await getStorageAddOns());
            } catch (error) {
                Toast.error(error.getApiErrorDetails().message);
                setOptions([]);
            }
        }

        if (show) {
            setSelectedOption(null);
            setPaymentMethod(PAYMENT_METHOD.EXISTING);
            loadAddOns();
        }
    }, [show]);

    const finishUpgrade = async option => {
        await onPurchased();
        Toast.success(`Payment successful. Your storage cap is now ${option.amountLabel}.`);
        onClose();
    };

    const purchaseWithExistingCard = async selectedCard => {
        if (!selectedCard) {
            setPaymentMethod(PAYMENT_METHOD.NEW);
            return;
        }

        await purchaseStorageUpgrade(selectedOption.addonUuid, selectedCard);
        await finishUpgrade(selectedOption);
    };

    const confirmDowngrade = async () => {
        setIsSubmitting(true);
        try {
            const response = await scheduleStorageDowngrade(selectedOption.addonUuid);
            const pending = response.pendingStorageDowngrade;
            const effectiveDate = formatDate(pending.effectiveAt);
            Toast.success(
                `Your current ${pending.currentStorageGb} GB cap stays active until ${effectiveDate}. ` +
                `It will then change to ${pending.newStorageGb} GB.`
            );
            await onPurchased();
            onClose();
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const cancelDowngrade = async () => {
        setIsSubmitting(true);
        try {
            await cancelStorageDowngrade();
            Toast.success(`Downgrade cancelled. Your ${summary.currentStorageCapGb} GB storage cap will remain active.`);
            await onPurchased();
            onClose();
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderOptionCard = option => {
        const isCurrent = option.changeType === "current";
        const isPending = summary.pendingStorageDowngrade?.newAddonUuid === option.addonUuid;
        const buttonLabel = isCurrent
            ? "Current plan"
            : isPending
                ? "Cancel downgrade"
                : option.changeType === "upgrade"
                    ? "Upgrade"
                    : "Downgrade";

        return (
            <PortalMetricCard
                key={option.id}
                minHeight="16rem"
                border={isCurrent ? "2px solid var(--calendar-text, #171A22)" : undefined}
            >
                <PortalMetricLabel>{isPending ? "Scheduled downgrade" : "Storage plan"}</PortalMetricLabel>
                <PortalMetricValue size="1.75rem">{option.amountLabel}</PortalMetricValue>
                <StandardModalText weight="700" color="var(--calendar-text, #171A22)">
                    {option.priceLabel}
                </StandardModalText>
                <StandardModalText size="0.8125rem">{option.description}</StandardModalText>
                <StandardModalActionButton
                    type="button"
                    width="100%"
                    minWidth="0"
                    $variant={isPending ? "danger" : "primary"}
                    disabled={isCurrent || isSubmitting}
                    onClick={isPending ? cancelDowngrade : () => setSelectedOption(option)}
                >
                    {buttonLabel}
                </StandardModalActionButton>
            </PortalMetricCard>
        );
    };

    const renderDowngrade = () => {
        const effectiveDate = formatDate(summary.nextBillingCycleAt);

        return (
            <ConfirmWrap>
                <div>
                    <StandardModalHeading size="2rem" align="center">Confirm Storage Downgrade</StandardModalHeading>
                    <StandardModalDescription align="center">
                        Your current allowance remains available until the next billing cycle. No refund is issued for unused time.
                    </StandardModalDescription>
                </div>
                <TransitionGrid>
                    <StandardModalInfoCard>
                        <StandardModalText weight="700">Current cap</StandardModalText>
                        <PortalMetricValue size="1.75rem">{summary.currentStorageCapGb} GB</PortalMetricValue>
                        <StandardModalText>Active until {effectiveDate}</StandardModalText>
                    </StandardModalInfoCard>
                    <TransitionArrow aria-hidden="true">→</TransitionArrow>
                    <StandardModalInfoCard background="var(--calendar-surface-secondary, #F3F5FB)">
                        <StandardModalText weight="700">New cap</StandardModalText>
                        <PortalMetricValue size="1.75rem">{selectedOption.amountLabel}</PortalMetricValue>
                        <StandardModalText>Effective {effectiveDate}</StandardModalText>
                    </StandardModalInfoCard>
                </TransitionGrid>
                <StandardModalActionGroup $center>
                    <StandardModalActionButton type="button" onClick={() => setSelectedOption(null)} disabled={isSubmitting}>
                        Back
                    </StandardModalActionButton>
                    <StandardModalActionButton type="button" $variant="primary" onClick={confirmDowngrade} disabled={isSubmitting}>
                        {isSubmitting ? "Scheduling..." : "Confirm"}
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            </ConfirmWrap>
        );
    };

    const renderUpgrade = () => {
        const pricing = selectedOption.proratedPricing;
        const currency = pricing?.currency || "AUD";

        return (
            <>
                <StandardModalHeading size="2rem" align="center">Complete Storage Upgrade</StandardModalHeading>
                <StandardModalDescription align="center">
                    Payment must complete before your storage cap changes from {summary.currentStorageCapGb} GB to {selectedOption.amountLabel}.
                </StandardModalDescription>
                <StandardModalFormGrid columns="repeat(2, minmax(0, 1fr))" gap="1rem">
                    <StandardModalSection padding="1.5rem" gap="1rem">
                        <StandardModalInfoCard>
                            <StandardModalText weight="700">Storage cap</StandardModalText>
                            <StandardModalText>{summary.currentStorageCapGb} GB → {selectedOption.amountLabel}</StandardModalText>
                            <StandardModalText>Prorated through {formatDate(pricing?.endsAt)}</StandardModalText>
                        </StandardModalInfoCard>
                        <PortalPaymentSummaryCard>
                            <PortalPaymentSummaryRow>
                                <PortalPaymentSummaryLabel>Subtotal</PortalPaymentSummaryLabel>
                                <PortalPaymentSummaryAmount>{formatMoney(pricing?.subtotal, currency)}</PortalPaymentSummaryAmount>
                            </PortalPaymentSummaryRow>
                            <PortalPaymentSummaryRow>
                                <PortalPaymentSummaryLabel>Processing fee</PortalPaymentSummaryLabel>
                                <PortalPaymentSummaryAmount>{formatMoney(pricing?.processingFee, currency)}</PortalPaymentSummaryAmount>
                            </PortalPaymentSummaryRow>
                            <PortalPaymentSummaryRow>
                                <PortalPaymentSummaryLabel>GST</PortalPaymentSummaryLabel>
                                <PortalPaymentSummaryAmount>{formatMoney(pricing?.gst, currency)}</PortalPaymentSummaryAmount>
                            </PortalPaymentSummaryRow>
                            <PortalPaymentSummaryRow>
                                <PortalPaymentSummaryLabel>Total due now</PortalPaymentSummaryLabel>
                                <PortalPaymentSummaryAmount size="1.25rem">{formatMoney(pricing?.total, currency)}</PortalPaymentSummaryAmount>
                            </PortalPaymentSummaryRow>
                        </PortalPaymentSummaryCard>
                        <StandardModalActionButton type="button" onClick={() => setSelectedOption(null)}>Back</StandardModalActionButton>
                    </StandardModalSection>
                    <StandardModalSection padding="1.5rem" gap="1rem">
                        <PortalPaymentOptionGrid>
                            <PaymentSelectionButton
                                $active={paymentMethod === PAYMENT_METHOD.EXISTING}
                                onClick={() => setPaymentMethod(PAYMENT_METHOD.EXISTING)}
                            >
                                <PaymentSelectionIcon $active={paymentMethod === PAYMENT_METHOD.EXISTING} />
                                Use an existing card
                            </PaymentSelectionButton>
                            <PaymentSelectionButton
                                $active={paymentMethod === PAYMENT_METHOD.NEW}
                                onClick={() => setPaymentMethod(PAYMENT_METHOD.NEW)}
                            >
                                <PaymentSelectionIcon $active={paymentMethod === PAYMENT_METHOD.NEW} />
                                Add new card
                            </PaymentSelectionButton>
                        </PortalPaymentOptionGrid>
                        {paymentMethod === PAYMENT_METHOD.EXISTING ? (
                            <CardList
                                token={token}
                                onBackClicked={() => setSelectedOption(null)}
                                onPurchaseClicked={purchaseWithExistingCard}
                                onDeleteClicked={null}
                            />
                        ) : (
                            <AirwallexForm
                                productType={AirwallexProduct.STORAGE_ADDON}
                                token={token}
                                companyName={companyName}
                                email={email}
                                abn={abn}
                                addonUuid={selectedOption.addonUuid}
                                nextClick={() => finishUpgrade(selectedOption)}
                                backToPrevious={() => setPaymentMethod(PAYMENT_METHOD.EXISTING)}
                            />
                        )}
                    </StandardModalSection>
                </StandardModalFormGrid>
            </>
        );
    };

    return (
        <StandardModal open={show} onClose={onClose} widthMode="wide" bodyPadding="0" hideCloseButton>
            <StandardModalSection padding="2.5rem" border="0" borderRadius="0" background="transparent" gap="2rem">
                {selectedOption?.changeType === "downgrade" ? renderDowngrade() : selectedOption ? renderUpgrade() : (
                    <>
                        <div>
                            <StandardModalHeading size="2rem" align="center">Choose Your Storage Plan</StandardModalHeading>
                            <StandardModalDescription align="left" maxWidth="44rem">
                                Upgrades are charged at a prorated amount and activate after payment. Downgrades take effect on your next billing date.
                            </StandardModalDescription>
                        </div>
                        {summary.pendingStorageDowngrade && (
                            <StandardModalInfoCard background="var(--calendar-surface-secondary, #F3F5FB)">
                                <StandardModalText weight="700">Scheduled storage change</StandardModalText>
                                <StandardModalText>
                                    Your current {summary.pendingStorageDowngrade.currentStorageGb} GB cap will change to {summary.pendingStorageDowngrade.newStorageGb} GB on {formatDate(summary.pendingStorageDowngrade.effectiveAt)}.
                                </StandardModalText>
                            </StandardModalInfoCard>
                        )}
                        <PortalMetricGrid columns="5" gap="1rem">{options.map(renderOptionCard)}</PortalMetricGrid>
                        <StandardModalActionGroup $center>
                            <StandardModalActionButton type="button" onClick={onClose}>Close</StandardModalActionButton>
                        </StandardModalActionGroup>
                    </>
                )}
            </StandardModalSection>
        </StandardModal>
    );
}
